import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";

/**
 * The dates-and-obligations register (ops module 1). A director keeps what is due, who owns it and what
 * evidences it. It states no legal requirement: the starter headings are generic and marked UNVERIFIED.
 * Residents never see this page, only counts elsewhere.
 */

const STATUS_STYLE = {
    overdue: "bg-red-50 text-red-700 border-red-200",
    due_soon: "bg-amber-50 text-amber-800 border-amber-200",
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    no_evidence: "bg-slate-100 text-slate-700 border-slate-300",
};

const EMPTY = { title: "", category: "other", due_date: "", owner: "", notes: "" };

export default function DatesRegister() {
    const [data, setData] = useState({ items: [], summary: null, labels: {} });
    const [template, setTemplate] = useState({ items: [], note: "", categories: [] });
    const [documents, setDocuments] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [evidence, setEvidence] = useState({});

    const load = useCallback(async () => {
        try {
            const [reg, tpl, docs] = await Promise.all([
                api.get("/register"), api.get("/register/template"), api.get("/documents"),
            ]);
            setData(reg.data);
            setTemplate(tpl.data);
            setDocuments(Array.isArray(docs.data) ? docs.data : []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load the register");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const add = async (e) => {
        e.preventDefault();
        try {
            await api.post("/register", {
                ...form, owner: form.owner || null, notes: form.notes || null,
            });
            setForm(EMPTY);
            toast.success("Added to the register");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add the item");
        }
    };

    const complete = async (item) => {
        try {
            await api.post(`/register/${item.id}/complete`, { evidence_document_id: evidence[item.id] || null });
            toast.success("Marked done");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not mark it done");
        }
    };

    const remove = async (item) => {
        try {
            await api.delete(`/register/${item.id}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete the item");
        }
    };

    const s = data.summary;
    return (
        <div className="max-w-3xl mx-auto px-6 py-10" data-testid="register-page">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <CalendarClock size={14} /> Dates and obligations
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">What is due, and what evidences it</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                Keep the dates that matter to your estate: renewals, contract ends, checks, accounts. Each item has an
                owner and, once done, the document that shows it. This page does not say what the law requires of you.
            </p>

            {s && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="register-summary">
                    {["overdue", "due_soon", "ok", "no_evidence"].map((k) => (
                        <div key={k} className={`rounded-lg border p-3 ${STATUS_STYLE[k]}`}>
                            <div className="text-2xl font-bold">{s[k]}</div>
                            <div className="text-xs">{data.labels[k]}</div>
                        </div>
                    ))}
                </div>
            )}

            <ul className="mt-6 space-y-3" data-testid="register-list">
                {data.items.length === 0 && (
                    <li className="text-sm text-slate-600 border border-dashed border-slate-300 rounded-lg p-6 text-center" data-testid="register-empty">
                        Nothing on the register yet. Add an item below, or start from a heading.
                    </li>
                )}
                {data.items.map((item) => (
                    <li key={item.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`register-item-${item.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="font-semibold text-slate-900">{item.title}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    {item.category.replace("_", " ")} · due {item.due_date}
                                    {item.owner ? ` · ${item.owner}` : ""}
                                </div>
                                {item.notes && <p className="text-sm text-slate-600 mt-1">{item.notes}</p>}
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_STYLE[item.status]}`} data-testid={`register-status-${item.id}`}>
                                {data.labels[item.status]}
                            </span>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            {!item.completed_at && (
                                <>
                                    <select
                                        className="border rounded px-2 py-1 text-xs max-w-[14rem]"
                                        value={evidence[item.id] || ""}
                                        onChange={(e) => setEvidence({ ...evidence, [item.id]: e.target.value })}
                                        aria-label="Evidence document"
                                        data-testid={`register-evidence-${item.id}`}
                                    >
                                        <option value="">No evidence document</option>
                                        {documents.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                                    </select>
                                    <button className="rounded bg-slate-900 text-white px-3 py-1 text-xs" onClick={() => complete(item)} data-testid={`register-complete-${item.id}`}>
                                        Mark done
                                    </button>
                                </>
                            )}
                            <button className="text-xs underline text-slate-500" onClick={() => remove(item)}>Delete</button>
                        </div>
                    </li>
                ))}
            </ul>

            <form onSubmit={add} className="mt-8 rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="register-form">
                <h2 className="font-heading text-lg font-semibold text-slate-900">Add an item</h2>
                <div className="flex flex-wrap gap-1.5">
                    {template.items.map((t) => (
                        <button type="button" key={t.title} className="text-xs border border-slate-200 rounded-full px-2.5 py-1 text-slate-600 hover:bg-slate-50"
                            onClick={() => setForm({ ...form, title: t.title, category: t.category })}>
                            {t.title}
                        </button>
                    ))}
                </div>
                {template.note && <p className="text-xs text-slate-500" data-testid="register-template-note">{template.note}</p>}
                <input className="w-full border rounded px-3 py-2 text-sm" placeholder="What is it?" required maxLength={120}
                    value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="register-title" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select className="border rounded px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} aria-label="Category">
                        {template.categories.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
                    </select>
                    <input className="border rounded px-3 py-2 text-sm" type="date" required value={form.due_date}
                        onChange={(e) => setForm({ ...form, due_date: e.target.value })} aria-label="Due date" data-testid="register-due" />
                    <input className="border rounded px-3 py-2 text-sm" placeholder="Owner (optional)" maxLength={80}
                        value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
                </div>
                <textarea className="w-full border rounded px-3 py-2 text-sm" rows={2} maxLength={500} placeholder="Notes (optional)"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                <button className="rounded bg-slate-900 text-white px-4 py-2 text-sm" type="submit" data-testid="register-add">Add to the register</button>
            </form>
        </div>
    );
}
